import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FileText, Clock, CheckCircle, AlertTriangle,
  Play, Trophy, Target,
  BookOpen, Info, ChevronRight,
  ArrowLeft, Monitor, Wifi, Volume2,
  Award, Zap, Calendar, Shield, GraduationCap
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import toast from 'react-hot-toast';
import apiClient from '@/services/api';
import { API_ENDPOINTS } from '@/config/api.config';
import { getExamQuestionCount, getExamTotalPointsLabel } from '@/utils/examQuestionCount';

interface CertificationExamDetails {
  _id: string;
  certification_id: string;
  title: string;
  description?: string;
  banner_image_url?: string;
  thumbnail_image_url?: string;
  questions: any[];
  total_points: number;
  passing_score: number;
  time_limit_minutes?: number;
  max_attempts?: number;
  shuffle_questions: boolean;
  shuffle_options: boolean;
  is_published: boolean;
  generated_by: string;
  createdAt: Date;
  updatedAt: Date;
}

interface SystemCheckItem {
  name: string;
  status: 'checking' | 'passed' | 'failed';
  icon: React.ElementType;
}

export default function LearnerExams() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [certificationData, setCertificationData] = useState<CertificationExamDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'instructions' | 'systemcheck' | 'ready'>('overview');
  const [systemChecks, setSystemChecks] = useState<SystemCheckItem[]>([
    { name: 'Internet Connection', status: 'checking', icon: Wifi },
    { name: 'Browser Compatibility', status: 'checking', icon: Monitor },
    { name: 'Audio Output', status: 'checking', icon: Volume2 }
  ]);
  const [isReadyToStart, setIsReadyToStart] = useState(false);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BookOpen },
    { id: 'instructions', label: 'Instructions', icon: Info },
    { id: 'systemcheck', label: 'System Check', icon: Monitor },
    { id: 'ready', label: 'Ready', icon: Play }
  ];

  // Fetch certification details from API
  const loadCertificationDetails = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      const response = await apiClient.get(API_ENDPOINTS.CERTIFICATIONS.GET_DETAILS(id));

      if (response.data.success) {
        setCertificationData(response.data.data);
        console.log('📋 Certification Data:', response.data.data);
      }
    } catch (error) {
      console.error('Error loading certification:', error);
      toast.error('Failed to load certification details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadCertificationDetails();
  }, [loadCertificationDetails]);

  // Perform system checks
  useEffect(() => {
    if (activeTab === 'systemcheck') {
      performSystemChecks();
    }
  }, [activeTab]);

  const performSystemChecks = async () => {
    // Reset checks
    setSystemChecks([
      { name: 'Internet Connection', status: 'checking', icon: Wifi },
      { name: 'Browser Compatibility', status: 'checking', icon: Monitor },
      { name: 'Audio Output', status: 'checking', icon: Volume2 }
    ]);

    // Simulate system checks with delays
    setTimeout(() => {
      setSystemChecks(prev => prev.map((check, idx) =>
        idx === 0 ? { ...check, status: 'passed' as const } : check
      ));
    }, 500);

    setTimeout(() => {
      setSystemChecks(prev => prev.map((check, idx) =>
        idx === 1 ? { ...check, status: 'passed' as const } : check
      ));
    }, 1000);

    setTimeout(() => {
      setSystemChecks(prev => prev.map((check, idx) =>
        idx === 2 ? { ...check, status: 'passed' as const } : check
      ));
      setIsReadyToStart(true);
    }, 1500);
  };

  const handleStartExam = () => {
    // TODO: Navigate to actual exam taking page
    toast.success('Starting exam...');
    // navigate(`/learn/exams/take/${id}`);
  };

  const handleGoBack = () => {
    navigate('/learn/certifications');
  };

  const handleNextTab = () => {
    const currentIndex = tabs.findIndex(tab => tab.id === activeTab);
    if (currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1].id as any);
    }
  };

  const handlePreviousTab = () => {
    const currentIndex = tabs.findIndex(tab => tab.id === activeTab);
    if (currentIndex > 0) {
      setActiveTab(tabs[currentIndex - 1].id as any);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading exam details...</p>
        </div>
      </div>
    );
  }

  if (!certificationData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Award className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">Certification not found</p>
          <button
            onClick={handleGoBack}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={handleGoBack}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg hover:bg-white/20 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back</span>
            </button>
          </div>

          <div className="flex items-center gap-3 mb-4">
            <Trophy className="w-8 h-8" />
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">{certificationData.title}</h1>
              <p className="text-purple-100 text-sm md:text-base">Certification Exam</p>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4" />
                <p className="text-xs text-purple-100">Duration</p>
              </div>
              <p className="text-lg font-bold">{certificationData.time_limit_minutes || 60} min</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-4 h-4" />
                <p className="text-xs text-purple-100">Questions</p>
              </div>
              <p className="text-lg font-bold">{getExamQuestionCount(certificationData)}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Target className="w-4 h-4" />
                <p className="text-xs text-purple-100">Passing Score</p>
              </div>
              <p className="text-lg font-bold">{certificationData.passing_score}%</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-4 h-4" />
                <p className="text-xs text-purple-100">Total Points</p>
              </div>
              <p className="text-lg font-bold">{getExamTotalPointsLabel(certificationData)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-4 md:px-6 py-3 md:py-4 border-b-2 font-medium transition-colors text-sm md:text-base whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4 md:w-5 md:h-5" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-6 md:py-8">
        <div className="max-w-4xl mx-auto">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="bg-white rounded-xl shadow-sm p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <BookOpen className="w-6 h-6 text-purple-600" />
                <h2 className="text-2xl font-bold">Exam Overview</h2>
              </div>

              <div className="prose max-w-none mb-6">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {certificationData.description || 'This certification exam validates your expertise and professional knowledge in the subject area.'}
                </ReactMarkdown>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-5 h-5 text-purple-600" />
                    <h3 className="font-semibold text-purple-900">Exam Structure</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-purple-800">
                    <li>• {getExamQuestionCount(certificationData)} questions to answer</li>
                    <li>• {certificationData.time_limit_minutes || 60} minutes time limit</li>
                    <li>• {getExamTotalPointsLabel(certificationData)} total points available</li>
                    <li>• {certificationData.passing_score}% score required to pass</li>
                  </ul>
                </div>

                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-5 h-5 text-amber-600" />
                    <h3 className="font-semibold text-amber-900">Exam Settings</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-amber-800">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Questions {certificationData.shuffle_questions ? 'will be' : 'will not be'} shuffled
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Options {certificationData.shuffle_options ? 'will be' : 'will not be'} shuffled
                    </li>
                    {certificationData.max_attempts && (
                      <li className="flex items-center gap-2">
                        <Zap className="w-4 h-4" />
                        {certificationData.max_attempts} attempt(s) allowed
                      </li>
                    )}
                  </ul>
                </div>
              </div>

              <div className="p-6 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 mb-3">
                  <Trophy className="w-5 h-5 text-green-600" />
                  <h3 className="font-semibold text-green-900">Upon Completion</h3>
                </div>
                <p className="text-green-800 text-sm">
                  Successfully passing this certification exam will earn you a professional certificate
                  that validates your skills and knowledge. This can boost your career prospects and
                  demonstrate your expertise to employers and peers.
                </p>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={handleNextTab}
                  className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition-colors"
                >
                  Next: Instructions
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Instructions Tab */}
          {activeTab === 'instructions' && (
            <div className="bg-white rounded-xl shadow-sm p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <Info className="w-6 h-6 text-purple-600" />
                <h2 className="text-2xl font-bold">Exam Instructions</h2>
              </div>

              <div className="space-y-6">
                {/* General Instructions */}
                <div>
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    Before You Begin
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium">Ensure a stable internet connection</p>
                        <p className="text-sm text-gray-600">Your exam progress will be saved automatically, but a stable connection is required.</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium">Find a quiet environment</p>
                        <p className="text-sm text-gray-600">Choose a distraction-free location where you can focus for the duration of the exam.</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium">Allocate sufficient time</p>
                        <p className="text-sm text-gray-600">You have {certificationData.time_limit_minutes || 60} minutes. Make sure you won't be interrupted.</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium">Use a compatible browser</p>
                        <p className="text-sm text-gray-600">Chrome, Firefox, Safari, or Edge (latest versions) are recommended.</p>
                      </div>
                    </li>
                  </ul>
                </div>

                {/* During the Exam */}
                <div>
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-purple-600" />
                    During the Exam
                  </h3>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <ul className="space-y-2 text-blue-900">
                      <li className="flex items-start gap-2">
                        <span className="font-bold mt-1">•</span>
                        <span>Read each question carefully before selecting your answer</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-bold mt-1">•</span>
                        <span>You can navigate between questions using Previous and Next buttons</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-bold mt-1">•</span>
                        <span>Your progress is saved automatically as you answer questions</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-bold mt-1">•</span>
                        <span>A timer will display your remaining time</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-bold mt-1">•</span>
                        <span>Review your answers before final submission</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Tips for Success */}
                <div className="p-6 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-2 mb-3">
                    <GraduationCap className="w-5 h-5 text-purple-600" />
                    <h3 className="font-semibold text-purple-900">Tips for Success</h3>
                  </div>
                  <ul className="space-y-2 text-purple-800 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="font-bold">1.</span>
                      <span>Manage your time - aim to spend about {Math.ceil((certificationData.time_limit_minutes || 60) / (getExamQuestionCount(certificationData) || 1))} minutes per question</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold">2.</span>
                      <span>If unsure about a question, mark it for review and move on</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold">3.</span>
                      <span>Trust your first instinct - changing answers often leads to mistakes</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold">4.</span>
                      <span>Stay calm and focused throughout the exam</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="flex justify-between gap-3 mt-6">
                <button
                  onClick={handlePreviousTab}
                  className="flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Previous
                </button>
                <button
                  onClick={handleNextTab}
                  className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition-colors"
                >
                  Next: System Check
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* System Check Tab */}
          {activeTab === 'systemcheck' && (
            <div className="bg-white rounded-xl shadow-sm p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <Monitor className="w-6 h-6 text-purple-600" />
                <h2 className="text-2xl font-bold">System Check</h2>
              </div>

              <p className="text-gray-600 mb-6">
                We're performing a quick check to ensure your system is ready for the exam.
              </p>

              <div className="space-y-4 mb-8">
                {systemChecks.map((check, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      check.status === 'passed'
                        ? 'bg-green-50 border-green-300'
                        : check.status === 'failed'
                        ? 'bg-red-50 border-red-300'
                        : 'bg-gray-50 border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <check.icon className={`w-5 h-5 ${
                          check.status === 'passed' ? 'text-green-600' :
                          check.status === 'failed' ? 'text-red-600' : 'text-gray-400'
                        }`} />
                        <span className="font-medium">{check.name}</span>
                      </div>
                      <div>
                        {check.status === 'checking' && (
                          <div className="w-5 h-5 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
                        )}
                        {check.status === 'passed' && (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                        {check.status === 'failed' && (
                          <AlertTriangle className="w-5 h-5 text-red-600" />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {isReadyToStart && (
                <div className="p-6 bg-green-50 rounded-lg border-2 border-green-300 mb-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-green-900 mb-2">All Systems Ready!</h3>
                      <p className="text-green-800 text-sm">
                        Your system has passed all checks. You're ready to begin your certification exam.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-between gap-3">
                <button
                  onClick={handlePreviousTab}
                  className="flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Previous
                </button>
                <button
                  onClick={handleNextTab}
                  disabled={!isReadyToStart}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                    isReadyToStart
                      ? 'bg-purple-600 text-white hover:bg-purple-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isReadyToStart ? 'Next: Ready to Start' : 'Waiting for checks...'}
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Ready Tab */}
          {activeTab === 'ready' && (
            <div className="bg-white rounded-xl shadow-sm p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-purple-100 rounded-full mb-4">
                  <Play className="w-10 h-10 text-purple-600" />
                </div>
                <h2 className="text-3xl font-bold mb-2">Ready to Begin?</h2>
                <p className="text-gray-600">
                  You're all set to start your certification exam
                </p>
              </div>

              {/* Final Summary */}
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 mb-8">
                <h3 className="font-semibold text-lg mb-4 text-center">Exam Summary</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <FileText className="w-5 h-5 text-purple-600" />
                      <span className="text-sm text-gray-600">Certification</span>
                    </div>
                    <p className="font-semibold">{certificationData.title}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <Clock className="w-5 h-5 text-purple-600" />
                      <span className="text-sm text-gray-600">Time Limit</span>
                    </div>
                    <p className="font-semibold">{certificationData.time_limit_minutes || 60} minutes</p>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <Target className="w-5 h-5 text-purple-600" />
                      <span className="text-sm text-gray-600">Questions</span>
                    </div>
                    <p className="font-semibold">{getExamQuestionCount(certificationData)} questions</p>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <Trophy className="w-5 h-5 text-purple-600" />
                      <span className="text-sm text-gray-600">Passing Score</span>
                    </div>
                    <p className="font-semibold">{certificationData.passing_score}%</p>
                  </div>
                </div>
              </div>

              {/* Important Reminders */}
              <div className="bg-amber-50 rounded-lg p-6 border-2 border-amber-200 mb-8">
                <div className="flex items-start gap-3 mb-4">
                  <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-amber-900 mb-2">Important Reminders</h3>
                    <ul className="space-y-2 text-sm text-amber-800">
                      <li>• Once you start, the timer will begin immediately</li>
                      <li>• You cannot pause the exam once started</li>
                      <li>• Make sure you have a stable internet connection</li>
                      <li>• Do not close or refresh your browser during the exam</li>
                      <li>• You must submit your exam before the time runs out</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex justify-between gap-3">
                <button
                  onClick={handlePreviousTab}
                  className="flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Previous
                </button>
                <button
                  onClick={handleStartExam}
                  className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 font-semibold text-lg transition-all shadow-lg hover:shadow-xl"
                >
                  <Play className="w-6 h-6" />
                  Start Exam Now
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
